// Sphinx Sarcophagus banish-pair helpers. Pure. No React, no Three.js.
//
// ── Codex (Sphinx Sarcophagus) ──
//   "Genesis → Banish two identical spells from cemeteries to draw a
//    spell."
//
//   • "Identical" = same printed name.
//   • "from cemeteries" = either player's Cemetery (the two need not come
//     from the same pile). A spell is any non-Site cemetery card (Sites
//     can't be cast as spells).
//
// GameBoard owns the imperative banish (Cemetery → Banished) + the
// cemetery picker + the draw. This module owns the read-only queries so
// the Genesis gate (is the cost payable?) and the pick filter share one
// source of truth.

/** Printed name of a cemetery card row via the normalized catalog. */
function cardName(card, catalog) {
  if (!card) return null;
  if (card.name) return card.name;
  const full = Array.isArray(catalog) ? catalog.find((c) => c?.unique_id === card.cardId) : null;
  return full?.name || null;
}

/** True iff a cemetery card row resolves to a non-Site (a castable spell). */
export function isSpellCard(card, catalog) {
  const full = Array.isArray(catalog) ? catalog.find((c) => c?.unique_id === card.cardId) : null;
  // Default to spell when the catalog lookup misses. Cemetery rows are
  // overwhelmingly spells, and the pick gate re-checks at click time.
  return !full || full.type !== 'Site';
}

/**
 * Spell rows across both cemeteries, each tagged with its resolved
 * printed name and the id of the pile it lives in (so the banisher can
 * splice it out of the right list).
 *
 * @param cemeteries [{ id, cards }]. Both players' Cemetery piles
 * @param catalog    normalized sorcery-cards catalog
 * @returns [{ card, name, pileId }]
 */
export function namedCemeterySpells(cemeteries, catalog) {
  const out = [];
  for (const pile of cemeteries || []) {
    for (const card of pile?.cards || []) {
      if (!isSpellCard(card, catalog)) continue;
      const name = cardName(card, catalog);
      if (!name) continue;
      out.push({ card, name, pileId: pile.id });
    }
  }
  return out;
}

/**
 * True iff some printed name appears on two-or-more spell rows across
 * the cemeteries. I.e. the banish-two-identical cost is payable.
 */
export function hasBanishablePair(cemeteries, catalog) {
  const counts = new Map();
  for (const { name } of namedCemeterySpells(cemeteries, catalog)) {
    const n = (counts.get(name) || 0) + 1;
    if (n >= 2) return true;
    counts.set(name, n);
  }
  return false;
}
